import { useTranslation } from 'react-i18next';

import { env } from '@/env';
import { useQlashUser } from '@/hooks/useQlashUser';
import { useGetWalletsQuery } from '@/services/qbankApi';
import { LevelBadge } from '@/components/hub/LevelBadge';

/**
 * Chi sei, sotto il tuo stendardo: nickname e strada verso il livello dopo.
 *
 * Sta qui e non nella barra in alto perché è la schermata che parla DI TE: in
 * alto quei due dati erano chrome, cioè accessori a qualunque cosa si stesse
 * facendo. Qui sono il contenuto, e possono avere la dimensione che meritano.
 *
 * ⚠️ I NUMERI DELL'ESPERIENZA SONO UN SEGNAPOSTO, e non per pigrizia: QBank
 * oggi **non può** dare questa barra. `/wallets/get` restituisce la DISTANZA dal
 * livello successivo (`toNextLevel`) ma non l'AMPIEZZA del livello, cioè manca
 * il denominatore — e senza denominatore una barra di riempimento non esiste.
 * In più le soglie della moneta esperienza sono seedate come valori assoluti
 * mentre QBank le somma come scarti, quindi anche il livello stesso è sballato
 * (vedi `docs/analysis/2026-07-17-ranked-circle-design.md`).
 *
 * Il LIVELLO invece è vero quando c'è: viene dal portafoglio, ed è lo stesso
 * numero che le altre schermate mostrano nella stella in alto. Solo se manca
 * (utente senza portafoglio, o modalità di prova) si ripiega sul segnaposto,
 * altrimenti la barra resterebbe vuota proprio dove va guardata.
 */

/** ⚠️ Segnaposto — sostituire il giorno che QBank espone l'ampiezza del livello. */
const EXP_MOCK = { current: 350, span: 500, level: 12 };

export function PlayerIdentity() {
  const { t } = useTranslation('general');

  // 🔴 UNA FONTE SOLA PER BUILD, e qui NON e' una preferenza di stile.
  // Questo file arriva dal restyle con `meFromStore ?? useGetMeQuery(...)`, cioe' con una
  // chiamata a `GET /users/me` sul monolite Laravel. E' un file NUOVO, quindi l'analisi
  // delle collisioni non lo segnalava: non ripristina niente che avessimo tolto, ne
  // aggiunge una dove non ce n'erano. Ma su una build Limulo (ADR-0078) Laravel non si
  // interroga affatto, e `me` viene da `GET /v1/me` — spazi di id diversi (numerico
  // contro ObjectId), non intercambiabili. `useQlashUser` sceglie la fonte in base al
  // flag di build. Il perche' esteso in `hooks/useQlashUser.ts`.
  const me = useQlashUser();

  const { data: wallets } = useGetWalletsQuery(undefined, { skip: !env.featureQbankEnabled });
  const realLevel = wallets?.find((w) => w.level != null)?.level ?? null;

  const level = realLevel ?? EXP_MOCK.level;
  const { current, span } = EXP_MOCK;
  const pct = span > 0 ? Math.max(0, Math.min(1, current / span)) * 100 : 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Nickname a sinistra, esperienza a destra, sulla stessa riga: sono le
          due etichette della barra qui sotto — chi sei e quanto manca — e
          incolonnate avrebbero occupato una riga in più per dire lo stesso.
          Niente ripiego sul nome legale o sull'email se il nickname manca: la
          stessa regola della barra in alto. */}
      <div className="flex items-end justify-between gap-3">
        <span className="min-w-0 truncate text-lg font-black leading-none text-fg">
          {me?.username ?? ''}
        </span>
        <span className="shrink-0 text-[11px] font-bold leading-none tabular-nums text-fg-muted">
          {current}/{span} {t('exp', { defaultValue: 'EXP' })}
        </span>
      </div>

      {/* Livello attuale ← barra → livello prossimo.
          I due capi sono DIVERSI di proposito: a sinistra la stella piena, che
          è il livello raggiunto (ed è la stessa stella che sta in alto in ogni
          altra sezione — qui non è sparita, si è spostata); a destra un gettone
          vuoto col contorno cucito, perché quel livello non è ancora tuo. Due
          stelle uguali avrebbero detto che li hai entrambi. */}
      <div className="flex items-center gap-2">
        <LevelBadge level={level} className="-my-1" />

        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={span}
          aria-valuenow={current}
          aria-label={t('exp_to_next', { defaultValue: 'Experience to next level' })}
          className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-pack-deep"
        >
          <span
            aria-hidden
            className="block h-full rounded-full bg-pack-fg transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>

        <span
          aria-label={t('level_next', { defaultValue: 'Next level' })}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-pack-stitch text-[11px] font-black leading-none tabular-nums text-pack-muted"
        >
          {level + 1}
        </span>
      </div>
    </div>
  );
}
